import { useEffect, useRef, useState, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '../store/useAuthStore';

/**
 * WebSocket Connection Hook
 * Handles Socket.io connection, authentication, reconnection, and event listening
 */

interface WebSocketConfig {
  url?: string;
  autoConnect?: boolean;
  debug?: boolean;
}

interface ConnectionStatus {
  connected: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * useWebSocket Hook
 * Manages WebSocket connection lifecycle
 */
export const useWebSocket = (config: WebSocketConfig = {}) => {
  const resolvedUrl =
    config.url ??
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? import.meta.env.VITE_API_URL || 'http://localhost:4000'
      : import.meta.env.VITE_API_URL
        ? String(import.meta.env.VITE_API_URL).replace(/\/api\/?$/, '')
        : undefined);
  const { autoConnect = true, debug = false } = config;

  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>({
    connected: false,
    error: null,
    retryCount: 0,
  });

  const { token } = useAuthStore();

  /**
   * Initialize socket connection
   */
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    try {
      const socket = resolvedUrl
        ? io(resolvedUrl, {
            auth: {
              token,
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 10,
          })
        : io({
            auth: {
              token,
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 10,
          });

      // Connection established
      socket.on('connect', () => {
        if (debug) console.log('WebSocket connected');
        setStatus({
          connected: true,
          error: null,
          retryCount: 0,
        });
      });

      // Reconnection successful
      socket.on('reconnect', () => {
        if (debug) console.log('WebSocket reconnected');
        setStatus((prev) => ({
          ...prev,
          connected: true,
          error: null,
        }));
      });

      // Connection lost
      socket.on('disconnect', (reason) => {
        if (debug) console.log('WebSocket disconnected:', reason);
        setStatus({
          connected: false,
          error: new Error(`Disconnected: ${reason}`),
          retryCount: 0,
        });
      });

      // Connection error
      socket.on('connect_error', (error) => {
        if (debug) console.error('WebSocket connection error:', error);
        setStatus((prev) => ({
          ...prev,
          connected: false,
          error,
          retryCount: prev.retryCount + 1,
        }));
      });

      // Authentication error
      socket.on('error', (error) => {
        if (debug) console.error('WebSocket error:', error);
        setStatus((prev) => ({
          ...prev,
          error: new Error(typeof error === 'string' ? error : 'Unknown error'),
        }));
      });

      socketRef.current = socket;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to initialize WebSocket');
      setStatus({
        connected: false,
        error: err,
        retryCount: 0,
      });
    }
  }, [resolvedUrl, token, debug]);

  /**
   * Disconnect socket
   */
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setStatus({
        connected: false,
        error: null,
        retryCount: 0,
      });
    }
  }, []);

  /**
   * Emit event to server
   */
  const emit = useCallback((event: string, data?: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    } else if (debug) {
      console.warn(`Cannot emit "${event}" - WebSocket not connected`);
    }
  }, [debug]);

  /**
   * Listen to event from server
   */
  const on = useCallback((event: string, callback: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  }, []);

  /**
   * Listen to event once
   */
  const once = useCallback((event: string, callback: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.once(event, callback);
    }
  }, []);

  /**
   * Stop listening to event
   */
  const off = useCallback((event: string, callback?: (data: any) => void) => {
    if (socketRef.current) {
      if (callback) {
        socketRef.current.off(event, callback);
      } else {
        socketRef.current.off(event);
      }
    }
  }, []);

  /**
   * Auto-connect on mount, disconnect on unmount
   */
  useEffect(() => {
    if (autoConnect && token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, token, connect, disconnect]);

  return {
    socket: socketRef.current,
    status,
    connect,
    disconnect,
    emit,
    on,
    once,
    off,
    isConnected: status.connected,
  };
};

/**
 * useRealtimeData Hook
 * Manages real-time data updates for a specific dashboard
 */
interface RealtimeDataHook<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  subscribe: (eventName: string) => void;
  unsubscribe: (eventName: string) => void;
}

export const useRealtimeData = <T,>(
  eventName: string,
  initialData: T | null = null,
  autoSubscribe = true
): RealtimeDataHook<T> => {
  const websocket = useWebSocket({ debug: false });
  const [data, setData] = useState<T | null>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Subscribe to real-time updates
   */
  const subscribe = useCallback(
    (event: string) => {
      if (websocket.isConnected) {
        websocket.on(event, (newData) => {
          setData(newData);
          setLoading(false);
          setError(null);
        });

        // Also listen for errors on this event
        websocket.on(`${event}:error`, (err) => {
          setError(err instanceof Error ? err : new Error(err));
          setLoading(false);
        });
      }
    },
    [websocket]
  );

  /**
   * Unsubscribe from real-time updates
   */
  const unsubscribe = useCallback(
    (event: string) => {
      websocket.off(event);
      websocket.off(`${event}:error`);
    },
    [websocket]
  );

  /**
   * Auto-subscribe on mount
   */
  useEffect(() => {
    if (autoSubscribe && websocket.isConnected) {
      setLoading(true);
      subscribe(eventName);
    }

    return () => {
      if (autoSubscribe) {
        unsubscribe(eventName);
      }
    };
  }, [autoSubscribe, eventName, subscribe, unsubscribe, websocket.isConnected]);

  return {
    data,
    loading,
    error,
    subscribe,
    unsubscribe,
  };
};
